import DeleteIcon from '@mui/icons-material/Delete';
import {
    Button,
    Grid,
    IconButton,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip
} from '@mui/material';
import { SyntheticEvent, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { UserDto } from '../../common/PricePrgClient';
import { StyledTabs } from '../../common/StyledComponents';
import { UserAvatar } from '../../common/UserAvatar';
import { RootState } from '../../store';
import { showConfirmDialog } from '../ui/redux/uiSlices';
import { getUsersWithGroups } from '../usergroup/redux/userGroupSelectors';
import InviteExternalUserDialog from './dialogs/InviteExternalUserDialog';
import InviteUserDialog from './dialogs/InviteUserDialog';
import {
    openInviteExternalUserDialog,
    openInviteInternalUserDialog,
    removeUserFromApp
} from './redux/userSlices';
const mapStateToProps = (state: RootState) => ({
    users: getUsersWithGroups(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const UserComponent = ({ users }: Props) => {
    const [tabIndex, setTabIndex] = useState(0);

    const dispatch = useDispatch();

    const filteredUsers = useMemo(() => {
        const userArr = users.sort((a, b) =>
            a.fullName.toLocaleLowerCase().localeCompare(b.fullName.toLocaleLowerCase())
        );

        if (tabIndex === 0) {
            return userArr.filter(user => user.isInternal);
        }
        if (tabIndex === 1) {
            return userArr.filter(user => !user.isInternal);
        }

        return userArr;
    }, [users, tabIndex]);

    const handleTabChange = (event: SyntheticEvent<Element, Event>, newTabIndex: number) => {
        setTabIndex(newTabIndex);
    };

    const handleOpenInviteDialog = () => {
        if (tabIndex === 0) {
            dispatch(openInviteInternalUserDialog(true));
        }
        if (tabIndex === 1) {
            dispatch(openInviteExternalUserDialog(true));
        }
    };

    const handleRemoveUser = (user: UserDto) => {
        dispatch(
            showConfirmDialog({
                dialogTitle: `Remove ${user.fullName} from Price Program`,
                dialogMessage: `Do you want to remove ${user.fullName} from Price Program?`,
                primaryActionName: 'Remove',
                okAction: removeUserFromApp.request(user.userId)
            })
        );
    };

    return (
        <>
            <Grid container direction="column" spacing={2} sx={{ maxWidth: 1000 }}>
                <Grid item>
                    <Grid container direction="row" justifyContent="space-between">
                        <Grid item>
                            <StyledTabs
                                value={tabIndex}
                                onChange={handleTabChange}
                                indicatorColor="primary"
                                textColor="primary"
                            >
                                <Tab label="Internal" />
                                <Tab label="External" />
                            </StyledTabs>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="primary" onClick={handleOpenInviteDialog}>
                                Invite user
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table" size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>Name</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Member of</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.map(user => (
                                    <TableRow key={user.userId}>
                                        <TableCell component="th" scope="row" style={{ width: 100 }}>
                                            <UserAvatar user={user} iconOnly={true} />
                                        </TableCell>
                                        <TableCell style={{ width: 250 }}>{user.fullName}</TableCell>
                                        <TableCell style={{ width: 200 }}>{user.username}</TableCell>
                                        <TableCell style={{ width: 300 }}>
                                            {user.groups.map(ug => ug.name).join(', ')}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={`Remove ${user.fullName} from Price Program`}>
                                                <IconButton
                                                    onClick={() => handleRemoveUser(user)}
                                                    color="secondary"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            <InviteUserDialog />
            <InviteExternalUserDialog />
        </>
    );
};

export default connect(mapStateToProps)(UserComponent);
